import _ from 'lodash'
import queryString from 'query-string'

function getShipmentItems(shipment) {
  const orderLineProperties = _.get(shipment, 'orderLineProperties', [])
  let data = []

  // add orderLines to data
  data = _.map(
    _.get(shipment, 'orderLines', []),
    (orderLine) => ({
      frontendItemType: 'orderLine',
      ...orderLine,
    }),
  )

  // add orderLineProperties to data
  data = [
    ...data,
    ..._.map(
      _.filter(
        orderLineProperties,
        'isMainProperty',
      ),
      (orderLineProperty) => {
        const addons = _.reject(
          _.filter(
            orderLineProperties,
            { groupUuid: _.get(orderLineProperty, 'groupUuid', '---group-uuid-not-found---') },
          ),
          'isMainProperty',
        )
        return {
          frontendItemType: 'orderLineProperty',
          ...orderLineProperty,
          ...(
            _.isEmpty(addons)
              ? {}
              : { payload: groupOrderLineAddonsWithQuantity(addons) }
          ),
        }
      },
    ),
  ]

  return data
}

function getShipmentFlow(shipment) {
  const requireDeliveryAddress = _.get(shipment, 'deliveryType.requireDeliveryAddress', false)
  const requireCourierService = _.get(shipment, 'deliveryType.requireCourierService', false)
  const requirePickupStore = _.get(shipment, 'deliveryType.requirePickupStore', false)
  let shipmentFlow = 'takeHome'
  if (requirePickupStore) shipmentFlow = 'pickup'
  if (requireDeliveryAddress) shipmentFlow = 'ship'
  if (
    shipmentFlow === 'pickup'
    && requireCourierService
  ) shipmentFlow = 'catering'
  return shipmentFlow
}

function getOrderOthers(priceDetails) {
  const orderOthers = _.get(priceDetails, 'orderOthers', [])
  return _.map(orderOthers, (item) => ({
    frontendItemType: 'orderOthersItem',
    ...item,
  }))
}

function getOrderSummary(order) {
  return {
    delivery:
      _.get(order, 'priceDetails.deliveryFee')
      || _.get(order, 'priceDetails.orderDeliveryFee')
      || _.get(order, 'shippingCharge'),
    serviceCharge:
      _.get(order, 'priceDetails.serviceCharge')
      || _.get(order, 'priceDetails.ordersServiceCharge')
      || _.get(order, 'serviceCharge'),
    vouchers: _.get(order, 'priceDetails.voucherAmount'),
    // discounts could be an array of discounts or a string value
    discounts:
      _.get(order, 'priceDetails.discountGroup')
      || _.get(order, 'priceDetails.orderDiscountGroup')
      || _.get(order, 'priceDetails.orderDiscount')
      || _.get(order, 'discountedPrice'),
    subtotal:
      _.get(order, 'priceDetails.subtotalPrice')
      || _.get(order, 'priceDetails.orderSubtotalPrice')
      || _.get(order, 'subtotalPrice'),
    // TODO: add tax
    // tax: 0,
    totalLoyaltyPoints: _.get(order, 'priceDetails.totalLoyaltyPoints'),
    total:
      _.get(order, 'priceDetails.totalPrice')
      || _.get(order, 'priceDetails.orderTotalPrice')
      || _.get(order, 'totalPrice'),
  }
}

function getPaymentInfo(order) {
  return _.map(_.get(order, 'paymentRequests'), (payment) => {
    let extraData = {}
    // TODO: need to generalise this part later
    // special handling for pay2Go orders with offline params (i.e. those orders in reserved status)
    const offlineParamsString = _.get(payment, 'paymentData.newebpayOfflineParams')
      || _.get(payment, 'paymentData.pay2GoOfflineParams', '')
    if (_.get(order, 'state') === 'reserved' && !_.isEmpty(offlineParamsString)) {
      const offlineParams = queryString.parse(offlineParamsString)
      const paymentType = _.get(offlineParams, 'PaymentType')
      // fields directly from API
      const paymentTypeFields = {
        VACC: 'PaymentType BankCode CodeNo Amt ExpireDate',
        CVS: 'PaymentType Amt ExpireDate CodeNo',
        BARCODE: 'PaymentType Amt ExpireDate Barcode_1 Barcode_2 Barcode_3',
      }
      let availableFields = []
      // put availabe fields in state for using in render
      if (paymentTypeFields[paymentType]) {
        availableFields = _.map(paymentTypeFields[paymentType].split(' ') || [], (field) => {
          const value = offlineParams[field] || ''
          switch (field) {
            case 'PaymentType':
              return {
                code: field,
                label: `services.orderService.reservedPayment.${payment.provider}.paymentType.${value}`,
                type: 'text',
                value,
              }
            case 'Amt':
              return {
                code: field,
                label: `services.orderService.reservedPayment.${payment.provider}.label.${field}`,
                type: 'price',
                value,
              }
            // using data url to display qrcode image
            case 'CodeNo':
              return {
                code: field,
                label: `services.orderService.reservedPayment.${payment.provider}.label.${field}`,
                type: paymentType === 'CVS' ? 'qrcode' : 'text',
                value,
              }
            default:
              return {
                code: field,
                label: `services.orderService.reservedPayment.${payment.provider}.label.${field}`,
                type: 'text',
                value,
              }
          }
        })
      }
      if (!_.isEmpty(availableFields)) {
        extraData = {
          paymentType,
          availableFields,
        }
      }
    }
    return {
      id: payment.id,
      method: payment.providerComputedName || payment.provider,
      statusCode: payment.state,
      amount: payment.paidAmount,
      extraData,
      // CP-20210503: This is very specific for pay2go reserved orders only. May change in
      // future once we have proper implementation from API side.
      // paymentQrCodeValue: _.get(order, 'state') === 'reserved'
      //   && !_.isEmpty(queryString.parse(_.get(payment, 'paymentData.pay2GoOfflineParams', '')))
      //   ? _.get(
      //     queryString.parse(_.get(payment, 'paymentData.pay2GoOfflineParams', '')),
      //     'CodeNo',
      //   )
      //   : null
    }
  })
}

function getPaymentType({ providerPaymentType, providerComputedName }) {
  if (providerPaymentType === 'VISA' || /visa/i.test(providerComputedName)) {
    return 'visa'
  }
  if (providerPaymentType === 'MASTER' || /master/i.test(providerComputedName)) {
    return 'master'
  }
  if (providerPaymentType === 'Apple Pay' || /apple pay/i.test(providerComputedName)) {
    return 'applePay'
  }
  if (/cash [^voucher]/i.test(providerComputedName)) {
    return 'cash'
  }
  return null
}

// async function findOrderByCartReferenceNumber(cartReferenceNumber) {
//   if (_.isEmpty(cartReferenceNumber)) {
//     console.error('findOrderByCartReferenceNumber: missing cart reference number')
//     return Promise.reject({ generalError: { code: 404 } })
//   }
//   return fetchOrders({
//     c: 'cart_reference_number',
//     q: cartReferenceNumber,
//   }).then(data => {
//     if (_.isEmpty(data.orders)) return Promise.reject({ generalError: { code: 404 } })
//     return { order: _.first(data.orders) }
//   })
// }

/**
 *
 * @param {array} addons an array of addons
 */
function groupOrderLineAddonsWithQuantity(addons = []) {
  const uniqAddons = _.uniqWith(addons, (a, b) => (
    _.isEqual(_.get(a, 'sku.id', 'a'), _.get(b, 'sku.id', 'b'))
    && _.isEqual(_.get(a, 'parentOrderLinePropertyId', 'a'), _.get(b, 'parentOrderLinePropertyId', 'b'))
  ))
  const addonsWithQuantity = _.map(uniqAddons, (addon) => {
    const items = _.filter(addons, (item) => (
      _.isEqual(_.get(addon, 'sku.id', 'a'), _.get(item, 'sku.id', 'b'))
      && _.isEqual(_.get(addon, 'parentOrderLinePropertyId', 'a'), _.get(item, 'parentOrderLinePropertyId', 'b'))
    ))
    const itemCount = _.sumBy(items, 'quantity')
    return {
      ...addon,
      quantity: itemCount,
    }
  })
  return addonsWithQuantity
}

export default {
  // findOrderByCartReferenceNumber,
  getOrderOthers,
  getOrderSummary,
  getPaymentInfo,
  getPaymentType,
  getShipmentFlow,
  getShipmentItems,
  groupOrderLineAddonsWithQuantity,
}
